import React, { useState } from "react";
import { Twitter, Facebook, Linkedin, MessageCircle, Mail, Link2, Check, Share2 } from "lucide-react";

/** Social share button row for any public page. Generates correct URLs for
 *  X/Twitter, Facebook, LinkedIn, WhatsApp, Email + a Copy-link button. Also
 *  invokes the native Web Share API on mobile devices for a single big button. */
export default function SocialShare({ url, title, summary = "" }) {
  const [copied, setCopied] = useState(false);
  const fullUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const encodedUrl = encodeURIComponent(fullUrl);
  const encodedTitle = encodeURIComponent(title || "");
  const encodedSummary = encodeURIComponent(summary);

  const share = (network) => {
    const targets = {
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedSummary}%0A%0A${encodedUrl}`,
    };
    const u = targets[network];
    if (!u) return;
    if (network === "email") window.location.href = u;
    else window.open(u, "_blank", "noopener,width=620,height=580");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* clipboard unavailable */ }
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title, text: summary, url: fullUrl });
    } catch (_) { /* user cancelled */ }
  };

  const buttons = [
    { net: "twitter",  icon: Twitter,       label: "X",        color: "hover:text-sky-400",     testid: "share-twitter" },
    { net: "facebook", icon: Facebook,      label: "Facebook", color: "hover:text-blue-400",    testid: "share-facebook" },
    { net: "linkedin", icon: Linkedin,      label: "LinkedIn", color: "hover:text-blue-500",    testid: "share-linkedin" },
    { net: "whatsapp", icon: MessageCircle, label: "WhatsApp", color: "hover:text-emerald-400", testid: "share-whatsapp" },
    { net: "email",    icon: Mail,          label: "Email",    color: "hover:text-emerald-400",   testid: "share-email" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="social-share">
      <span className="text-xs uppercase tracking-widest text-white/40 font-semibold mr-1">Share</span>
      {buttons.map(b => (
        <button
          key={b.net}
          onClick={() => share(b.net)}
          className={`p-2 rounded-full border border-white/10 text-white/65 transition-colors bg-white/[0.02] ${b.color}`}
          aria-label={`Share on ${b.label}`}
          data-testid={b.testid}
        >
          <b.icon className="w-4 h-4" />
        </button>
      ))}
      <button
        onClick={copy}
        className="p-2 rounded-full border border-white/10 text-white/65 hover:text-fuchsia-400 transition-colors bg-white/[0.02]"
        aria-label="Copy link"
        data-testid="share-copy-link"
      >
        {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Link2 className="w-4 h-4" />}
      </button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button
          onClick={nativeShare}
          className="p-2 rounded-full border border-white/10 text-white/65 hover:text-white transition-colors bg-white/[0.02]"
          aria-label="More share options"
          data-testid="share-native"
        >
          <Share2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
